// src/context/PWAContext.tsx
import React, { createContext, useContext, useState, useEffect } from "react";
import { pwaService } from "../services/pwaService";

interface InstallationInfo {
  displayMode: string;
  standalone: boolean;
  version?: string;
  platform?: string;
  browser?: string;
}

interface PWAContextType {
  isInstallable: boolean;
  isInstalled: boolean;
  showInstallModal: boolean;
  installInfo: InstallationInfo | null;
  handleInstall: () => Promise<void>;
  handleDismissInstall: () => void;
}

interface PWAState {
  isInstallable: boolean;
  isInstalled: boolean;
  showInstallModal: boolean;
  installInfo: InstallationInfo | null;
}

const initialState: PWAState = {
  isInstallable: false,
  isInstalled: false,
  showInstallModal: false,
  installInfo: null,
};

const PWAContext = createContext<PWAContextType | undefined>(undefined);

export const PWAProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<PWAState>(() => ({
    ...initialState,
    ...pwaService.getState(),
  }));

  useEffect(() => {
    pwaService.register();

    // Update installation info when the component mounts
    const updateInstallInfo = () => {
      const displayMode = window.matchMedia("(display-mode: standalone)")
        .matches
        ? "standalone"
        : "browser";

      const info: InstallationInfo = {
        displayMode,
        standalone: displayMode === "standalone",
        platform: navigator.platform,
        browser: detectBrowser(),
      };

      setState((prev) => ({ ...prev, installInfo: info }));
    };

    updateInstallInfo();
    window
      .matchMedia("(display-mode: standalone)")
      .addEventListener("change", updateInstallInfo);

    return () => {
      window
        .matchMedia("(display-mode: standalone)")
        .removeEventListener("change", updateInstallInfo);
    };
  }, []);

  const handleInstall = async () => {
    await pwaService.promptInstall();
    setState((prev) => ({ ...prev, ...pwaService.getState() }));
  };

  const handleDismissInstall = () => {
    pwaService.dismissInstall();
    setState((prev) => ({ ...prev, ...pwaService.getState() }));
  };

  const contextValue: PWAContextType = {
    isInstallable: state.isInstallable,
    isInstalled: state.isInstalled,
    showInstallModal: state.showInstallModal,
    installInfo: state.installInfo,
    handleInstall,
    handleDismissInstall,
  };

  return (
    <PWAContext.Provider value={contextValue}>{children}</PWAContext.Provider>
  );
};

const detectBrowser = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
    return "safari";
  } else if (userAgent.includes("chrome")) {
    return "chrome";
  } else if (userAgent.includes("firefox")) {
    return "firefox";
  }
  return "";
};

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (context === undefined) {
    throw new Error("usePWA must be used within a PWAProvider");
  }
  return context;
};
